import type { HealthDay } from '@pa/api/clinic'
import { AlertTriangle, CircleCheck, CircleHelp, OctagonAlert } from 'lucide-react'
import { createPortal } from 'react-dom'
import { incidentSpan, tooltipDate } from '../model/format'
import { fmtDuration, pctFromMinutes } from '../model/uptime'

/** Down minutes with no incident (Observability §5.4 "two numbers, by design"). */
export const BRIEF_INTERRUPTIONS = 'Brief interruptions, not long enough to count as an incident'

/** The tooltip's level line (UserFlows C-12): "No downtime recorded", or "Down X · YY.YY% uptime". */
export function dayLevelText(day: Pick<HealthDay, 'level' | 'upMinutes' | 'downMinutes'>): string {
  if (day.level === 'no_data') return 'No data'
  if (day.level === 'ok') return 'No downtime recorded'
  return `Down ${fmtDuration(day.downMinutes)} · ${pctFromMinutes(day.upMinutes, day.downMinutes) ?? '0.00'}% uptime`
}

const ICON = { ok: CircleCheck, minor: AlertTriangle, major: OctagonAlert, no_data: CircleHelp } as const

/**
 * C-12 day tooltip (UserFlows C-12, the mockup's `uptime-tip`): the date, the level line, and that day's incidents
 * (an incident spanning midnight shows on both days), or the "Brief interruptions…" note.
 */
export function DayTooltip({ day, timeZone, x, y }: { day: HealthDay; timeZone: string; x: number; y: number }) {
  const Icon = ICON[day.level]
  const levelClass = day.level === 'no_data' ? 'lv-text-none' : `lv-text-${day.level}`
  return createPortal(
    <div className="uptime-tip" style={{ left: x, top: y }} role="tooltip">
      <b>{tooltipDate(day.date)}</b>
      <div className={`ut-level ${levelClass}`}>
        <Icon aria-hidden />
        {dayLevelText(day)}
      </div>
      {day.incidents.map((i) => (
        <div key={i.id} className="ut-incident">
          {i.title}
          <span>{incidentSpan(i, timeZone)}</span>
        </div>
      ))}
      {day.downMinutes > 0 && day.incidents.length === 0 && <div className="ut-incident">{BRIEF_INTERRUPTIONS}</div>}
    </div>,
    document.body,
  )
}
